import './CompaniesPage.css';
import PageScaffold from '../../components/PageScaffold';
import { useContext, useEffect, useState } from 'react';
import CompaniesContext from '../../contexts/data/companies/CompaniesContext';
import {
  PlusOutlined,
  ReloadOutlined,
  ImportOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import CompaniesList from '../../components/@shared/companies/CompaniesList';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { billingIntervalAsString, degradedModeAsString, EBillingInterval, EDegradedMode, UserRole } from '@leedpulse-monorepo/enums';
import PageScaffoldContext from '../../contexts/ui/PageScaffoldContext';
import { Modal, message, Dropdown } from 'antd';
import { CompanyFilters } from '../../../domain/use_cases/companies/getCompaniesWithFilters';
import UsersContext from '../../contexts/data/UsersContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { useNavigate } from 'react-router-dom';
import searchCompanies from '../../../domain/use_cases/companies/searchCompanies';
import { ICompanyView } from '../../../domain/interfaces/ICompanyView';
import PaymentForm from '../../components/@shared/companies/payments/PaymentForm';
import discoverApimoAgencies from '../../../domain/use_cases/integrations/discoverApimoAgencies';

export const COMPANIES_PAGE_PATH = '/companies';
export const COMPANIES_PAGE_TITLE = 'Entreprises';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CompaniesPageProps { }

const CompaniesPage: React.FC<CompaniesPageProps> = (_) => {
  const companiesContext = useContext(CompaniesContext);
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const usersContext = useContext(UsersContext);
  const authContext = useContext(AuthenticationContext);

  const [companies, setCompanies] = useState<ICompanyView[]>([]);
  const [page, setPage] = useState<number>(0);
  const [nbHits, setNbHits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hitsPerPage, setHitsPerPage] = useState<number>(20);
  const [isModalCreatePaymentOpen, setIsModalCreatePaymentOpen] = useState<boolean>(false);
  const [isImportingApimo, setIsImportingApimo] = useState<boolean>(false);

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('companyFilters');
    return savedFilters
      ? JSON.parse(savedFilters)
      : ({
        employeesInCharge: [authContext.authentication?.id],
        status: 'active',
        degradedMode: undefined,
        billingInterval: undefined,
      } as CompanyFilters);
  });

  const pageScaffoldContentRef = pageScaffoldContext?.contentRef;
  const navigate = useNavigate();

  useEffect(() => {
    usersContext.fetchRoles([
      UserRole.admin,
      UserRole.maintainer,
      UserRole.owner,
    ]);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCompany(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters]);

  async function searchCompany(pageToFetch = 0, newHitsPerPage = hitsPerPage) {
    setIsLoading(true);
    try {
      const useCaseResponse = await searchCompanies(
        authContext.authentication,
        filters.query ?? '',
        pageToFetch,
        newHitsPerPage,
        {
          degradedMode: filters.degradedMode,
          billingInterval: filters.billingInterval
        },
      );
      setCompanies(useCaseResponse.hits);
      setNbHits(useCaseResponse.nbHits);
      setPage(pageToFetch);
    } catch (err) {
      console.error('[Algolia] erreur :', err);
    } finally {
      setIsLoading(false);
    }
  }

  const handleScrollToTop = () => {
    pageScaffoldContentRef?.current?.scrollTo(0, 0);
  };

  const updateFilters = (filterName: keyof typeof filters, value: any) => {
    setFilters((prevFilters: any) => ({ ...prevFilters, [filterName]: value }));
  };

  const handleRemoveCompany = async (companyId: string) => {
    await companiesContext.removeCompany(companyId);
    searchCompany(page);
  };

  const handleImportApimoAgencies = async () => {
    if (!authContext.authentication) return;
    setIsImportingApimo(true);
    try {
      const result = await discoverApimoAgencies(authContext.authentication);
      message.success(
        `Import Apimo terminé : ${result.discovered} agence(s) créée(s), ${result.skipped} ignorée(s)` +
        (result.errors.length ? `, ${result.errors.length} erreur(s)` : ''),
      );
      searchCompany(page);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Erreur lors de l'import des agences Apimo");
    } finally {
      setIsImportingApimo(false);
    }
  };

  return (
    <>
      <PageScaffold
        title={
          COMPANIES_PAGE_TITLE +
          ' (' +
          formatNumber(nbHits) +
          ')'
        }
        subtitle="Gérez les entreprises de l'application"
        breadcrumbItems={[
          {
            title: 'Entreprises',
          },
        ]}
        controlsSettings={{
          searchLabel: 'Recherche par nom du client',
          searchDefaultValue: filters.query ?? '',
          onSearch: (value) => updateFilters('query', value),
          isLoading: false,
          onSearchCancel: () => updateFilters('query', ''),
          actionButtons: [
            {
              label: 'Créer lien de paiement',
              type: 'primary',
              props: {
                icon: <PlusOutlined />,
                onClick: () => setIsModalCreatePaymentOpen(true),
              },
            },
            {
              label: 'Nouvelle entreprise',
              render: () => (
                <Dropdown.Button
                  type="primary"
                  icon={<MoreOutlined style={{ fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />}
                  loading={isImportingApimo}
                  onClick={() => navigate('/companies/new')}
                  menu={{
                    items: [
                      {
                        key: 'import-apimo',
                        label: 'Importer agences Apimo',
                        icon: <ImportOutlined />,
                        onClick: handleImportApimoAgencies,
                      },
                    ],
                  }}
                >
                  <PlusOutlined /> Nouvelle entreprise
                </Dropdown.Button>
              ),
            },
            {
              label: 'Rafraîchir',
              type: 'primary',
              props: {
                icon: <ReloadOutlined />,
                onClick: () => searchCompany(page),
              },
            },
          ],
        }}
        filterSettings={[{
          placeholder: 'Mode dégradé',
          clearable: true,
          selectedValue: filters.degradedMode ?? null,
          values: Object.values(EDegradedMode).map((mode) => ({ label: degradedModeAsString(mode), value: mode })),
          onFilter: (value) => updateFilters('degradedMode', value),
        },
        {
          placeholder: 'Périodicité',
          clearable: true,
          selectedValue: filters.billingInterval ?? null,
          values: Object.values(EBillingInterval).map((interval) => ({ label: billingIntervalAsString(interval), value: interval })),
          onFilter: (value) => updateFilters('billingInterval', value),
        }
        ]}
      >
        <CompaniesList
          companies={companies}
          loading={isLoading}
          page={page}
          nbHits={nbHits}
          hitsPerPage={hitsPerPage}
          onPageChange={(p) => searchCompany(p)}
          onScrollToTop={handleScrollToTop}
          onRemove={handleRemoveCompany}
          onPageSizeChange={(newPageSize) => {
            setHitsPerPage(newPageSize);
            searchCompany(0, newPageSize);
          }}
        />
      </PageScaffold>

      <Modal
        open={isModalCreatePaymentOpen}
        onCancel={() => setIsModalCreatePaymentOpen(false)}
        footer={null}
        width={570}
        destroyOnHidden
        getContainer={() => pageScaffoldContentRef?.current as HTMLElement} // Force modal portal to render inside the themed body
      >
        <PaymentForm />
      </Modal>
    </>
  );
};

export default CompaniesPage;
